import React, { useEffect, useRef, useState } from 'react';

const debounce = (func: Function, wait: number) => {
    let timeout: number;
    return function executedFunction(...args: any) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = window.setTimeout(later, wait);
    };
};

export type Size = { width: number; height: number };

export const AutoSize: React.FC<{
    children: (size: Size) => JSX.Element;
}> = ({ children }) => {
    const [size, setSize] = useState<Size>({ width: 1, height: 1 });
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // Create the debounced function once and reuse it
        const debouncedSetSize = debounce((width: number, height: number) => {
            setSize({ width, height });
        }, 100);

        const resizeObserver = new ResizeObserver(entries => {
            for (let entry of entries) {
                const { width, height } = entry.contentRect;
                // Call the debounced function
                debouncedSetSize(width, height);
            }
        });

        if (ref.current) {
            resizeObserver.observe(ref.current);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    return (
        <div ref={ref} style={{ height: '100%', width: '100%' }}>
            {children(size)}
        </div>
    );
};
